import { useState } from "react";
import * as Icons from "lucide-react";

/**
 * Inline share row — one-tap copy / WhatsApp / Email / SMS / X.
 * Pure frontend: builds standard `wa.me` / `mailto:` / `sms:` URLs from
 * the current page URL plus a customisable caption. No backend needed.
 *
 * Props:
 *   url      — The full URL to share. If omitted, uses window.location.href.
 *   title    — Title used as the share copy headline.
 *   subtitle — Optional second line (e.g. address).
 *   brand    — Brand color for the active state.
 */
export default function ShareRow({ url, title, subtitle, brand, className = "" }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const caption = `${title || "Check this listing out"}${subtitle ? ` — ${subtitle}` : ""}`;
  const fullText = `${caption}\n${shareUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback: select-and-copy via prompt
      try {
        window.prompt("Copy this link:", shareUrl);
      } catch {}
    }
  };

  const buttons = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: "MessageCircle",
      href: `https://wa.me/?text=${encodeURIComponent(fullText)}`,
      color: "#25D366",
    },
    {
      id: "email",
      label: "Email",
      icon: "Mail",
      href: `mailto:?subject=${encodeURIComponent(caption)}&body=${encodeURIComponent(fullText)}`,
      color: "#a1a1aa",
    },
    {
      id: "sms",
      label: "SMS",
      icon: "Smartphone",
      href: `sms:?&body=${encodeURIComponent(fullText)}`,
      color: "#a1a1aa",
    },
    {
      id: "x",
      label: "Share on X",
      icon: "Twitter",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`,
      color: "#a1a1aa",
    },
  ];

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="share-row">
      <button
        onClick={copy}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-all ${
          copied
            ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300"
            : "bg-white/[0.04] border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:border-white/30"
        }`}
        style={!copied && brand ? { borderColor: `${brand}40` } : undefined}
        data-testid="share-copy-link"
      >
        {copied ? (
          <>
            <Icons.Check size={12} />
            Copied
          </>
        ) : (
          <>
            <Icons.Link2 size={12} />
            Copy link
          </>
        )}
      </button>
      {buttons.map((b) => {
        const I = Icons[b.icon];
        return (
          <a
            key={b.id}
            href={b.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-white/[0.04] border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:border-white/30 text-xs transition-all"
            data-testid={`share-${b.id}`}
            title={b.label}
          >
            <I size={12} style={{ color: b.color }} />
            <span className="hidden sm:inline">{b.label}</span>
          </a>
        );
      })}
    </div>
  );
}
